import React, {useEffect} from "react";

import styles from "./LocationPage.module.css"
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {fetchLocationById, locationRequestStatusSelector, locationSelector} from "../locationSlice";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {Loader} from "../../../elements/loader/Loader";
import {LocationPageHeader} from "./components/LocationPageHeader/LocationPageHeader";
import {LocationDetails} from "./components/LocationDetails/LocationDetails";
import {VehicleInformation} from "./components/VehicleInformation/VehicleInformation";
import {updateLocationRequestStatusSelector} from "../locationUpdateSlice";
import {MenuItem, updateSelectedMenuItem} from "../../platform/platformSlice";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";
import {AftersalesInformation} from "./components/AftersalesInformation/AftersalesInformation";
import {EnquiryInformation} from "./components/EnquiryInformation/EnquiryInformation";
import {LocationEmailInformation} from "./components/LocationEmailInformation/LocationEmailInformation";
import {TabChooseButton} from "../../../elements/tabChooseButton/TabChooseButton";
import {KnowledgeRecordsTab} from "../../knowledge/KnowledgeRecordsTab";
import {KnowledgeRecordUi, UpdateKnowledgeRecordRequest} from "../../assistant/franchiseTypes";
import {updateKnowledgeRecords} from "../../knowledge/knowledgeApi";
import {KnowledgeScope} from "../../../../api/generated";

enum LocationPageTabs {
    DETAILS = "DETAILS",
    KNOWLEDGE = "KNOWLEDGE"
}

export const LocationPage: React.FC = () => {

    const navigate = useNavigate();
    const pageLocation = useLocation();
    const dispatch = useAppDispatch();
    const location = useAppSelector(locationSelector);
    const locationRequestStatus = useAppSelector(locationRequestStatusSelector);
    const updateLocationRequestStatus = useAppSelector(updateLocationRequestStatusSelector);

    const {locationId} = useParams<{ locationId?: string }>();

    useEffect(() => {
        if (locationId) {
            dispatch(fetchLocationById(locationId));
            dispatch(updateSelectedMenuItem(MenuItem.LOCATIONS))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId]);

    useEffect(() => {
        if (updateLocationRequestStatus === RequestStatus.RESOLVED && locationId) {
            dispatch(fetchLocationById(locationId));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateLocationRequestStatus]);


    useEffect(() => {
        if (locationRequestStatus === RequestStatus.REJECTED) {
            navigate("/locations")
            dispatch(showErrorToastNotification({message: "Failed to load location page"}))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationRequestStatus]);

    const currentTab = Object.values(LocationPageTabs).includes(pageLocation.hash.replace("#", "") as LocationPageTabs)
        ? pageLocation.hash.replace("#", "") as LocationPageTabs
        : LocationPageTabs.DETAILS;
    // Saving knowledge reloads the location. The knowledge tab stays mounted meanwhile, so it keeps its
    // page and the records still open for editing.
    const isReloadingKnowledge = currentTab === LocationPageTabs.KNOWLEDGE
        && locationRequestStatus === RequestStatus.PENDING
        && location?.publicId === locationId;

    if ((locationRequestStatus === RequestStatus.RESOLVED || isReloadingKnowledge) && location) {
        const saveKnowledgeRecords = async (knowledgeRecords: UpdateKnowledgeRecordRequest[]) => {
            await updateKnowledgeRecords({
                scope: KnowledgeScope.Location,
                locationPublicId: location.publicId,
                knowledgeRecords
            });
            if (locationId) {
                dispatch(fetchLocationById(locationId));
            }
        };

        return <div className={styles.wrapper}>
            <LocationPageHeader
                name={location.name}
                publicId={location.publicId}
                canBeDeleted={location.canBeDeleted}
                warnings={location.warnings ?? []}
            />
            <div className={styles.tabContainer}>
                <TabChooseButton
                    to={`#${LocationPageTabs.DETAILS}`}
                    replace={true}
                    imageSrc={"/assistantIcon.svg"}
                    text={"Details"}
                    isActive={currentTab === LocationPageTabs.DETAILS}
                />
                <TabChooseButton
                    to={`#${LocationPageTabs.KNOWLEDGE}`}
                    replace={true}
                    imageSrc={"/infoIcon.svg"}
                    text={"Knowledge"}
                    isActive={currentTab === LocationPageTabs.KNOWLEDGE}
                />
            </div>
            <div className={styles.dataWrapper}>
                {currentTab === LocationPageTabs.DETAILS && <>
                    <LocationDetails location={location}/>
                    <VehicleInformation location={location}/>
                    <EnquiryInformation location={location}/>
                    <LocationEmailInformation location={location}/>
                    <AftersalesInformation location={location}/>
                </>}
                {currentTab === LocationPageTabs.KNOWLEDGE &&
                    <KnowledgeRecordsTab
                        records={(location as typeof location & { knowledgeRecords?: KnowledgeRecordUi[] }).knowledgeRecords}
                        scope={KnowledgeScope.Location}
                        onSave={saveKnowledgeRecords}
                    />
                }
            </div>
        </div>
    } else {
        return <Loader/>
    }
}
